import React from 'react'
import { Card, Flex, Text } from '@tremor/react'
import { Crown, Medal, Award } from '@/components/icons'
import type { LeaderboardItem } from '../types'
import { cn } from '@/lib/utils'

interface LeaderboardProps {
  title: string
  items: LeaderboardItem[]
  metricSuffix?: string
  icon: React.ReactNode
  accentColor?: 'emerald' | 'blue' | 'violet'
}

const rankIcons = [
  <Crown key="1" className="w-4 h-4 text-amber-500" />,
  <Medal key="2" className="w-4 h-4 text-zinc-400" />,
  <Award key="3" className="w-4 h-4 text-amber-700" />,
]

const accentStyles = {
  emerald: {
    gradient: 'from-emerald-500 to-emerald-600',
    light: 'bg-emerald-50 text-emerald-700',
    border: 'border-emerald-200',
    borderLeft: 'border-l-emerald-500',
    bg: 'bg-gradient-to-br from-emerald-50/30 via-white to-white',
  },
  blue: {
    gradient: 'from-blue-500 to-blue-600',
    light: 'bg-blue-50 text-blue-700',
    border: 'border-blue-200',
    borderLeft: 'border-l-blue-500',
    bg: 'bg-gradient-to-br from-blue-50/30 via-white to-white',
  },
  violet: {
    gradient: 'from-violet-500 to-violet-600',
    light: 'bg-violet-50 text-violet-700',
    border: 'border-violet-200',
    borderLeft: 'border-l-violet-500',
    bg: 'bg-gradient-to-br from-violet-50/30 via-white to-white',
  },
}

export function Leaderboard({
  title,
  items,
  metricSuffix = '',
  icon,
  accentColor = 'blue'
}: LeaderboardProps) {
  const accent = accentStyles[accentColor]

  return (
    <Card className={cn(
      "border-l-4 shadow-md hover:shadow-lg p-0 overflow-hidden animate-fade-in transition-shadow duration-200",
      accent.borderLeft,
      accent.bg,
      "border-t border-r border-b",
      accent.border
    )}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-100">
        <Flex alignItems="center" className="gap-3">
          <div className={cn("p-2 rounded-lg", accent.light)}>
            {icon}
          </div>
          <Text className="text-zinc-950 font-semibold text-lg">{title}</Text>
        </Flex>
      </div>

      {/* List */}
      <div className="divide-y divide-zinc-50">
        {items.length > 0 ? (
          items.map((item, index) => (
            <div
              key={item.id}
              className={cn(
                "px-6 py-4 flex items-center gap-4 transition-colors",
                index === 0 ? "bg-amber-50/50" : "hover:bg-zinc-50"
              )}
            >
              {/* Rank */}
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center font-mono font-bold text-sm",
                index === 0
                  ? "bg-gradient-to-br from-amber-400 to-amber-500 text-white shadow-md"
                  : index === 1
                    ? "bg-zinc-200 text-zinc-700"
                    : index === 2
                      ? "bg-amber-100 text-amber-800"
                      : "bg-zinc-100 text-zinc-500"
              )}>
                {index + 1}
              </div>

              {/* Avatar */}
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm",
                `bg-gradient-to-br ${accent.gradient}`
              )}>
                {item.avatarInitials}
              </div>

              {/* Name */}
              <div className="flex-1 min-w-0">
                <Text className={cn(
                  "font-medium truncate",
                  index === 0 ? "text-zinc-950" : "text-zinc-700"
                )}>
                  {item.operatorName}
                </Text>
              </div>

              {/* Value */}
              <span className={cn(
                "font-mono font-bold text-lg",
                index === 0 ? "text-zinc-950" : "text-zinc-600"
              )}>
                {item.value.toFixed(metricSuffix === '%' ? 1 : 0)}{metricSuffix}
              </span>

              {/* Trophy Icon for Top Position */}
              {index < 3 && (
                <div className="w-5">
                  {rankIcons[index]}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="px-6 py-12 text-center">
            <Text className="text-zinc-400">No data available</Text>
          </div>
        )}
      </div>
    </Card>
  )
}
